<script>
import FailableEcText from "./FailableEcText";
import PrimaryButton from "@/components/PrimaryButton";

export default {
  name: "HeaderChallengeDisplay",
  components: {
    FailableEcText,
    PrimaryButton,
  },
  data() {
    return {
      activityTokens: [],
      infinityUnlocked: false,
      showExit: false,
      exitText: "",
      resetCelestial: false,
      inPelle: false,
    };
  },
  computed: {
    parts() {
      // We need activityToken for NC/IC/EC because plain check of WhateverChallenge.isRunning
      // won't trigger display update if we, say, switch from one challenge to another
      function celestialReality(celestial, name, tab) {
        return {
          name: () => `${name} Reality`,
          isActive: (token) => token,
          activityToken: () => celestial.isRunning,
          tabName: () => tab,
        };
      }
      return [
        celestialReality(Teresa, "Teresa's", "teresa"),
        celestialReality(Effarig, "Effarig's", "effarig"),
        celestialReality(Enslaved, "The Nameless Ones'", "enslaved"),
        celestialReality(V, "V's", "v"),
        celestialReality(Ra, "Ra's", "ra"),
        celestialReality(Laitela, "Lai'tela's", "laitela"),
        {
          name: (token) => `Space Challenge ${token}`,
          isActive: (token) => token > 0,
          activityToken: () => player.challenge.space.current,
        },
        {
          name: () => "Time Dilation",
          isActive: (token) => token,
          activityToken: () => player.dilation.active,
        },
        {
          name: (token) => `Eternity Challenge ${token}`,
          isActive: (token) => token > 0,
          activityToken: () => player.challenge.eternity.current,
        },
        {
          name: () =>
            `Mirror (${formatPercents(player.light.redPercent / 100)},${formatPercents(
              player.light.greenPercent / 100
            )},${formatPercents(player.light.bluePercent / 100)})`,
          isActive: (token) => token,
          activityToken: () => player.light.inMirror,
        },
        {
          name: (token) => `Infinity Challenge ${token}`,
          isActive: (token) => token > 0,
          activityToken: () => player.challenge.infinity.current,
        },
        {
          name: (token) => `${NormalChallenge(token).config.name} Challenge`,
          isActive: (token) => token > 0,
          activityToken: () => player.challenge.normal.current,
        },
      ];
    },
    activeChallengeNames() {
      const names = [];
      for (let i = 0; i < this.activityTokens.length; i++) {
        const token = this.activityTokens[i];
        const part = this.parts[i];
        if (!part.isActive(token)) continue;
        if (part.name(token).includes("Space Challenge")) {
          const currSC = player.challenge.space.current;
          const nextCompletion = SpaceChallenge(currSC).completions + 1;
          const maxCompletions = SpaceChallenge(currSC).maxCompletions;
          let completionText = "";
          if (nextCompletion > maxCompletions) {
            completionText = `(already completed for now)`;
          } else {
            completionText = `(${formatInt(nextCompletion)}/${formatInt(maxCompletions)})`;
          }
          names.push(`${part.name(token)} ${completionText}`);
        } else if (part.name(token).includes("Eternity Challenge")) {
          const currEC = player.challenge.eternity.current;
          const nextCompletion = EternityChallenge(currEC).completions + 1;
          let completionText = "";
          if (Enslaved.isRunning && currEC === 1) {
            completionText = `(${formatInt(nextCompletion)}/???)`;
          } else if (nextCompletion === 6) {
            completionText = `(already completed)`;
          } else {
            completionText = `(${formatInt(nextCompletion)}/${formatInt(5)})`;
          }
          names.push(`${part.name(token)} ${completionText}`);
        } else {
          names.push(part.name(token));
        }
      }
      return names;
    },
    isVisible() {
      return this.infinityUnlocked || this.activeChallengeNames.length > 0;
    },
    isInFailableEC() {
      return this.activeChallengeNames.some((str) => str.match(/Eternity Challenge (4|12)/gu));
    },
    challengeDisplay() {
      if (this.inPelle && this.activeChallengeNames.length > 0) {
        return `${this.activeChallengeNames.join(" + ")} in a Doomed Reality. Good luck.`;
      }
      if (this.inPelle) return "a Doomed Reality. Good luck.";
      if (this.activeChallengeNames.length === 0) {
        return "the Antimatter Universe (no active challenges)";
      }
      return this.activeChallengeNames.join(" + ");
    },
  },
  methods: {
    update() {
      this.infinityUnlocked = PlayerProgress.infinityUnlocked();
      this.activityTokens = this.parts.map((part) => part.activityToken());
      // Dilation in Pelle can't be left once entered, but we still want to allow leaving more nested challenges
      this.showExit =
        this.inPelle && player.dilation.active
          ? this.activeChallengeNames.length > 1
          : this.activeChallengeNames.length !== 0;
      this.exitText = this.exitDisplay();
      this.resetCelestial = player.options.retryCelestial;
      this.inPelle = Pelle.isDoomed;
    },
    // Process exit requests from the inside out; Challenges first, then dilation, then Celestial Reality. If the
    // relevant option is toggled, we pass a bunch of information over to a modal - otherwise we immediately exit
    exitButtonClicked() {
      let names, clickFn;
      const isEC = Player.anyChallenge instanceof EternityChallengeState;
      const isSC = Player.anyChallenge instanceof SpaceChallengeState;

      // Dilation and ECs can't be exited independently and we have a special dilation-exit modal, so we have
      // to treat that particular case differently. The dilation modal itself will account for EC state
      if (player.dilation.active && (!Player.isInAnyChallenge || isEC)) {
        if (player.options.confirmations.dilation) Modal.exitDilation.show();
        else startDilatedEternityRequest();
        return;
      }

      if (player.light.inMirror && !Player.isInAntimatterChallenge) {
        reverseMirror();
        return;
      }

      if (Player.isInAnyChallenge) {
        // Regex replacement is used to remove the "(X/Y)" which appears after ECs. The ternary statement is there
        // because this path gets called for NCs, ICs, and ECs
        const toExit = this.activeChallengeNames[this.activeChallengeNames.length - 1].replace(/\W+\(.*\)/u, "");
        names = { chall: toExit, normal: isEC ? "Eternity" : isSC ? "Game" : "Infinity" };
        clickFn = () => {
          const oldChall = Player.anyChallenge;
          Player.anyChallenge.exit(false);
          if (player.options.retryChallenge && !isSC && !isSCRunningOnTier(6, 1)) oldChall.requestStart();
        };
      } else {
        names = { chall: this.activeChallengeNames[0], normal: "Reality" };
        clickFn = () => beginProcessReality(getRealityProps(true));
      }

      if (player.options.confirmations.exitChallenge) {
        Modal.exitChallenge.show({
          challengeName: names.chall,
          normalName: names.normal,
          hasHigherLayers: this.inPelle || this.activeChallengeNames.length > 1,
          exitFn: clickFn,
        });
      } else {
        clickFn();
      }
    },
    // Bring the player to the tab related to the innermost challenge
    textClicked() {
      if (this.activeChallengeNames.length === 0) return;

      // Iterating back-to-front and breaking ensures we get the innermost restriction
      let fullName = "",
        celestial = "";
      for (let i = this.activityTokens.length - 1; i >= 0; i--) {
        const token = this.activityTokens[i];
        const part = this.parts[i];
        if (!part.isActive(token)) continue;
        fullName = part.name(token);
        celestial = part.tabName?.();
        break;
      }

      // Normal challenges are matched with an end-of-string metacharacter
      if (fullName.match(" Challenge$")) Tab.challenges.normal.show(true);
      else if (fullName.match("Infinity Challenge")) Tab.challenges.infinity.show(true);
      else if (player.light.inMirror) Tab.space.mirror.show(true);
      else if (fullName.match("Eternity Challenge")) Tab.challenges.eternity.show(true);
      else if (fullName.match("Space Challenge")) Tab.challenges.space.show(true);
      else if (player.dilation.active) Tab.eternity.dilation.show(true);
      else Tab.celestials[celestial].show(true);
    },
    exitDisplay() {
      if (Player.isInAntimatterChallenge) return (player.options.retryChallenge && !isSCRunningOnTierOrHigher(6, 1)) ? "Retry Challenge" : "Exit Challenge";
      if (player.light.inMirror) return canBreakMirror() ? "Break The Mirror" : "Escape The Mirror";
      if (Player.anyChallenge instanceof SpaceChallengeState) return "Exit Challenge"
      if (Player.isInAnyChallenge) return (player.options.retryChallenge && !isSCRunningOnTierOrHigher(6, 1)) ? "Retry Challenge" : "Exit Challenge";
      if (player.dilation.active) return "Exit Dilation";
      if (this.resetCelestial) return "Restart Reality";
      return "Exit Reality";
    },
    textClassObject() {
      return {
        "l-challenge-display": true,
        "l-challenge-display--clickable": this.activeChallengeNames.length !== 0,
      };
    },
  },
};
</script>

<template>
  <div v-if="isVisible" class="l-game-header__challenge-text">
    <span :class="textClassObject()" @click="textClicked"> You are currently in {{ challengeDisplay }} </span>
    <FailableEcText v-if="isInFailableEC" />
    <span class="l-padding-line" />
    <PrimaryButton v-if="showExit" @click="exitButtonClicked">
      {{ exitText }}
    </PrimaryButton>
  </div>
</template>

<style scoped>
.l-game-header__challenge-text {
  display: flex;
  height: 2rem;
  top: 50%;
  justify-content: center;
  align-items: center;
  font-size: 1.2rem;
  font-weight: bold;
  color: var(--color-text);
  margin: 0.5rem;
}

.l-challenge-display {
  padding: 0.5rem;
  cursor: default;
}

.l-challenge-display--clickable {
  cursor: pointer;
  user-select: none;
}

.l-challenge-display--clickable:hover {
  text-decoration: underline;
}

.l-padding-line {
  padding: 0.3rem;
}
</style>
